import { create } from 'zustand';
import type { ClientQuestion, RoundResult, MatchResult, GamePhase, GameMode } from '@wot/shared';
import { MODES, survivalTimerSeconds, roundClockSeconds, MAX_CLOCK_MS, dailyKey, type ModeId, type MatchModeId } from '@wot/shared';
import type { AnswerEvent } from '../lib/cognitiveEngine';
import { saveMatchToLocalAtlas } from '../lib/atlasData';
import { getGuestName } from '../lib/guestName';
import { getDeviceId } from '../lib/deviceId';
import { logAppEvent } from '../lib/appEvents';
import { refreshReminders } from '../lib/dailyReminder';
import { awardBadges, type BadgeSync } from '../lib/badges';
import { supabase, invokeFunction } from '../supabase';
import { trackGameStarted, trackQuestionAnswered, trackGameCompleted } from '../utils/analytics';

// ─── Anti-Repeat: localStorage question history ─────
const RECENT_KEY = 'wot_recent_questions';
const MAX_RECENT = 200;

function getRecentQuestionIds(): string[] {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); }
  catch { return []; }
}

function saveRecentQuestionIds(newIds: string[]) {
  const recent = [...getRecentQuestionIds(), ...newIds].slice(-MAX_RECENT);
  localStorage.setItem(RECENT_KEY, JSON.stringify(recent));
}

export interface ReviewEntry {
  questionId: string;
  roundIndex: number;
  realm: string;
  question: string;
  choices: string[];
  correctIndex: number;
  myChoice: number | null;   // null = timed out
  correct: boolean;
  explanation: string;
}

// ─── Views ──────────────────────────────────────
export type AppView = 'home' | 'lobby' | 'versus' | 'game' | 'results' | 'config' | 'profile' | 'leaderboard' | 'admin';

// ─── State Interface ────────────────────────────
interface GameState {
  // App
  view: AppView;
  nickname: string;
  playerId: string | null;
  /** Proof that this seat is ours. The seat id is public; only the function
   *  that seated us returns the token, and submit-answer asks for it back.
   *  Null from a server that has not started issuing tokens. */
  seatToken: string | null;
  error: string | null;
  gameMode: GameMode | null;
  /**
   * The arena being picked on the setup screen: 'ALL', a bucket id, or a
   * single realm id (custom). Kept under its historical name because every
   * screen already reads it.
   */
  selectedRealm: 'ALL' | string;
  /** The mode picked on the setup screen. */
  selectedMode: ModeId;
  /** The running match's recipe; null on legacy or pre-mode matches. */
  matchMode: MatchModeId | null;
  matchBucket: string | null;
  /** SURVIVAL lives remaining; null outside survival. */
  livesLeft: number | null;
  /**
   * Scoreboard totals AS OF THE LAST REVIEW. The live totals update the
   * moment an answer is scored, and showing that during an active round
   * leaks correctness before the reveal (your own, and worse, your
   * opponent's: a bar that jumps mid-round says they got it right). The
   * board shows these until the review phase makes the live totals public.
   */
  boardMyScore: number;
  boardOppScore: number;

  // Room
  roomId: string | null;
  inviteCode: string | null;
  phase: GamePhase;
  /** True for the player who created the room. Decides who proposes the start. */
  isHost: boolean;
  /** Set once both seats are confirmed present on the channel. */
  opponentPresent: boolean;
  /** This player pressed READY on the versus screen. */
  localReady: boolean;
  /** The opponent's READY, carried over presence. */
  opponentReady: boolean;
  /** Wall-clock instant both clients begin round one. Published by the host
   *  only, and only once both players have pressed READY. */
  matchStartAt: number | null;
  players: { id: string; nickname: string; connected: boolean }[];
  currentRound: number;
  totalRounds: number;
  scores: Record<string, number>;

  // Game stats
  correctAnswers: number;
  totalResponseTimeMs: number;
  questions: ClientQuestion[];
  currentQuestion: ClientQuestion | null;
  selectedAnswer: number | null;
  answerLocked: boolean;
  opponentAnswered: boolean;
  // The opponent's channel presence vanished mid-match: their clock no
  // longer matters, every remaining round reviews on the local answer.
  opponentLeft: boolean;
  // Opponent identity + live state (multiplayer). Data arrives peer-to-peer
  // over Realtime because submit-answer only returns the caller's own result.
  opponentId: string | null;
  opponentNickname: string | null;
  opponentScore: number;
  opponentLastCorrect: boolean | null; // this round; shown only in review
  roundResult: RoundResult | null;
  matchResult: MatchResult | null;
  timerEndsAt: number | null;
  // Local wall-clock time the current round became visible on THIS device.
  // Response time for scoring is measured from here (own clock only), so a
  // clock difference between two phones can't bias the speed bonus.
  roundStartedAt: number | null;
  countdownValue: number | null;
  // Per-answer event log for THIS match — feeds the Session Read (and later
  // the Cognitive Atlas). Real observed sequence; nothing is fabricated.
  answerLog: AnswerEvent[];
  // The match's questions as the player saw them, with the reveal: what the
  // post-match review shows for a miss. Kept only for the running match.
  reviewLog: ReviewEntry[];
  /** Right answers in a row as submit-answer counts them (0 after a miss). */
  streak: number;
  /** The STREAK BONUS share of this match's points, as submit-answer reports it. */
  streakBonusTotal: number;
  /** The badge count from the end of this match; null until it arrives (or if it fails). */
  badgeSync: BadgeSync | null;

  // Solo
  personalBest: number | null;
  pmPromotionsEnabled: boolean;
  realmCounts: Record<string, { total: number; easy: number; medium: number; hard: number }>;
  realmCountsLoaded: boolean;

  // Actions
  setNickname: (name: string) => void;
  setView: (view: AppView) => void;
  setError: (error: string | null) => void;
  setGameMode: (mode: GameMode) => void;
  setSelectedRealm: (realm: 'ALL' | string) => void;
  setSelectedMode: (mode: ModeId) => void;
  submitAnswer: (choiceIndex: number) => void;
  nextRound: (forceTime?: number, targetRound?: number) => void;
  startSoloMatch: (realmId?: string) => Promise<void>;
  startDailyMatch: () => Promise<void>;
  createRoom: () => Promise<void>;
  /** via says how the code arrived: a link or a typed code is a friend's
   *  invitation (TRAVEL_COMPANION counts it); quick match passes nothing. */
  joinRoom: (inviteCode: string, via?: 'link' | 'code') => Promise<void>;
  quickMatch: () => Promise<void>;
  resolveOpponent: () => Promise<void>;
  checkAndTriggerReview: () => void;
  resetGame: () => void;
  /** Walk out of a match. In a 1v1 the opponent is told first, then the
   *  channel closes; presence alone proved unreliable for that news. */
  leaveMatch: () => Promise<void>;

  // Lifecycle (will be wired to Supabase Realtime later)
  fetchSettings: () => Promise<void>;
  fetchRealmCounts: () => Promise<void>;
  initListeners: () => void;
  scheduleMatchStart: (startAt: number) => void;
  abandonRoomIfAlone: () => Promise<void>;
  beginVersus: (opponentNickname?: string | null) => void;
  declareReady: () => void;
  pollReadyState: (declare?: boolean) => Promise<void>;
  checkForOpponent: () => Promise<void>;
  finishMatch: () => Promise<void>;
  /** Asks the server to count this player's badges now that the match is over. */
  claimBadges: () => Promise<void>;
  cleanupListeners: () => void;
}

// The countdown length itself lives server-side now (ready_handshake arms
// round_one_at at now() + 4s); this remains only as the floor for a poll that
// lands after the armed instant.

// ── Poll throttling ───────────────────────────────────────────────────
// Two timers watch the handshake on purpose (the store's own, and the lobby
// screen's, which survives anything the store does to its listeners). They
// must not both hit the database: whoever fired most recently wins and the
// other's tick is a no-op. At 10k concurrent players this is the difference
// between matchmaking costing one cheap indexed query per waiting player per
// 1.2s and costing double that for no information gain.
const POLL_GAP_MS = 1200;
let lastSeatCheck = 0;
let lastReadyPoll = 0;

// ─── Store ──────────────────────────────────────
export const useGameStore = create<GameState>((set, get) => ({
  // Initial State
  view: 'home',
  nickname: '',
  playerId: null,
  seatToken: null,
  error: null,
  gameMode: null,
  selectedRealm: 'ALL',
  selectedMode: 'CLIMB',
  matchMode: null,
  matchBucket: null,
  livesLeft: null,
  boardMyScore: 0,
  boardOppScore: 0,

  roomId: null,
  inviteCode: null,
  phase: 'waiting',
  isHost: false,
  opponentPresent: false,
  localReady: false,
  opponentReady: false,
  matchStartAt: null,
  players: [],
  currentRound: 0,
  totalRounds: 10,
  scores: {},

  correctAnswers: 0,
  totalResponseTimeMs: 0,

  questions: [],
  currentQuestion: null,
  selectedAnswer: null,
  answerLocked: false,
  opponentAnswered: false,
  opponentLeft: false,
  opponentId: null,
  opponentNickname: null,
  opponentScore: 0,
  opponentLastCorrect: null,
  roundResult: null,
  matchResult: null,
  timerEndsAt: null,
  roundStartedAt: null,
  countdownValue: null,
  answerLog: [],
  reviewLog: [],
  streak: 0,
  streakBonusTotal: 0,
  badgeSync: null,

  personalBest: null,
  pmPromotionsEnabled: false,
  realmCounts: {},
  realmCountsLoaded: false,

  // ─── Actions ─────────────────────────────
  setNickname: (name) => set({ nickname: name }),
  // A view change must not swallow an error: the failed action sets error
  // and view together, the router then echoes the view back through here,
  // and clearing error on that echo blanked every banner before a player
  // could read it. Errors are cleared by the banner (tap or timer) and by
  // the start actions themselves.
  setView: (view) => set({ view }),
  fetchSettings: async () => {
    // Global PsycheMatrix promotions flag. Defaults OFF (fail-safe: no ads
    // until an admin explicitly enables them, e.g. once PsycheMatrix launches).
    const { data } = await supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'pm_promotions_enabled')
      .maybeSingle();
    set({ pmPromotionsEnabled: data?.value === true });
  },
  fetchRealmCounts: async () => {
    // Prefetched at app start so the config screen has per-realm counts ready
    // (no "..." -> number flash when a player opens Solo/Room setup).
    if (get().realmCountsLoaded) return;
    let page = 0;
    let hasMore = true;
    const counts: Record<string, { total: number; easy: number; medium: number; hard: number }> = {};
    while (hasMore) {
      // Count ONLY what the game can actually serve (get_random_questions
      // filters active + reviewed). Without these filters the config screen
      // over-reported the pool, so a game could return fewer questions than
      // the realm's displayed count implied.
      const { data } = await supabase
        .from('questions')
        .select('realm, difficulty')
        .eq('active', true)
        .eq('reviewed', true)
        .range(page * 1000, (page + 1) * 1000 - 1);
      if (data && data.length > 0) {
        data.forEach((q: any) => {
          if (!counts[q.realm]) counts[q.realm] = { total: 0, easy: 0, medium: 0, hard: 0 };
          counts[q.realm].total++;
          const diff = (q.difficulty || 'medium') as 'easy' | 'medium' | 'hard';
          if (counts[q.realm][diff] !== undefined) counts[q.realm][diff]++;
        });
        hasMore = data.length === 1000;
        page++;
      } else {
        hasMore = false;
      }
    }
    set({ realmCounts: counts, realmCountsLoaded: true });
  },
  setError: (error) => set({ error }),
  setGameMode: (mode) => set({ gameMode: mode }),
  setSelectedMode: (mode) => set({ selectedMode: mode }),
  setSelectedRealm: (realm) => set({ selectedRealm: realm }),

  checkAndTriggerReview: () => {
    const { phase, answerLocked, opponentAnswered, gameMode, roundResult, currentRound } = get();
    // Prevent double triggers
    if (phase !== 'round_active') return;

    if (gameMode === 'solo') {
      if (roundResult) {
        set({ phase: 'round_review' });

        const playerId = get().playerId;
        const wrong = playerId ? roundResult.players[playerId]?.correct === false : false;

        // Survival: a wrong answer or timeout costs a life; the third ends
        // the run. Ending reuses the same trick as endurance did: pinning
        // totalRounds to here makes the next advance the game-over path, so
        // depth reached and every stat read the run's true length.
        if (get().matchMode === 'SURVIVAL' && wrong) {
          const left = (get().livesLeft ?? 1) - 1;
          set({ livesLeft: left });
          if (left <= 0) {
            setTimeout(() => {
              set({ totalRounds: currentRound });
              get().nextRound(undefined, currentRound + 1);
            }, 3000);
            return;
          }
        }

        const targetNext = currentRound + 1;
        setTimeout(() => get().nextRound(undefined, targetNext), 3000);
      }
    } else {
      if (answerLocked && (opponentAnswered || get().opponentLeft) && roundResult) {
        set({ phase: 'round_review' });
        // Refresh the opponent's authoritative score for this review (robust
        // even if they broadcast an old-style payload without a score).
        get().resolveOpponent();
        const targetNext = currentRound + 1;
        setTimeout(() => get().nextRound(undefined, targetNext), 3500);
      }
    }
  },

  submitAnswer: async (choiceIndex) => {
    const { answerLocked, roomId, playerId, seatToken, currentQuestion, currentRound, timerEndsAt, gameMode } = get();
    if (answerLocked || !roomId || !playerId || !currentQuestion) return;

    // Anında kilitle
    set({ selectedAnswer: choiceIndex, answerLocked: true });

    // Measure reaction time from when THIS device showed the round, using
    // only this device's clock. Never derive it from the shared (absolute)
    // timerEndsAt, which lives in the other device's clock frame and made the
    // speed bonus depend on cross-device clock skew (host consistently won).
    const startedAt = get().roundStartedAt;
    const responseTimeMs = startedAt
      ? Math.min(MAX_CLOCK_MS, Math.max(0, Date.now() - startedAt))
      : (timerEndsAt ? Math.max(0, MAX_CLOCK_MS - (timerEndsAt - Date.now())) : 5000);

    try {
      const data = await invokeFunction('submit-answer', {
        matchId: roomId, playerId, seatToken, questionId: currentQuestion.id, roundNumber: currentRound, choiceIndex, responseTimeMs
      });

      if (data?.error) throw new Error(data.error);

      // Puanı state'e ekle
      const mine = data.roundResult.players[playerId];
      const pointsEarned = mine?.pointsEarned || 0;
      const isCorrect = mine?.correct || false;
      const myNewTotal = (get().scores[playerId] || 0) + pointsEarned;
      // The server's own count of the run; an older server sends none, and
      // the local count is the same thing without the bonus behind it.
      const streakNow = typeof mine?.streak === 'number' ? mine.streak : (isCorrect ? get().streak + 1 : 0);
      const streakBonus = typeof mine?.streakBonus === 'number' ? mine.streakBonus : 0;

      set((state) => ({
        scores: { ...state.scores, [playerId]: myNewTotal },
        correctAnswers: state.correctAnswers + (isCorrect ? 1 : 0),
        totalResponseTimeMs: state.totalResponseTimeMs + responseTimeMs,
        streak: streakNow,
        streakBonusTotal: state.streakBonusTotal + streakBonus,
        roundResult: data.roundResult,
        // Observed answer event (feeds the Session Read / Atlas).
        answerLog: [...state.answerLog, {
          realm: (currentQuestion.realm || 'UNKNOWN').toUpperCase(),
          difficulty: (currentQuestion.difficulty as 'easy' | 'medium' | 'hard') || 'medium',
          correct: isCorrect,
          responseTimeMs: Math.min(MAX_CLOCK_MS, Math.max(0, responseTimeMs)),
          roundIndex: currentRound,
          timedOut: choiceIndex === -1,
        }],
        reviewLog: [...state.reviewLog, {
          questionId: currentQuestion.id,
          roundIndex: currentRound,
          realm: (currentQuestion.realm || '').toUpperCase(),
          question: currentQuestion.question,
          choices: [...currentQuestion.choices],
          correctIndex: data.roundResult.correctIndex,
          myChoice: choiceIndex === -1 ? null : choiceIndex,
          correct: isCorrect,
          explanation: data.roundResult.explanation || '',
        }],
      }));

      // Tell the opponent I answered, and carry my identity + this round's
      // result + running score. The opponent's client shows my name always,
      // my running score, and (only in the review phase) my correct/wrong.
      // Broadcasting my own correctness reveals no answer index, so it is
      // consistent with the existing trust model.
      if (gameMode !== 'solo') {
        const channel = (window as any).activeGameChannel;
        if (channel) {
          channel.send({
            type: 'broadcast',
            event: 'player_answered',
            payload: {
              playerId,
              nickname: get().nickname || getGuestName(),
              correct: isCorrect,
              totalScore: myNewTotal,
            },
          });
        }
      }

      // Analytics: question answered
      trackQuestionAnswered(currentQuestion.id, isCorrect, responseTimeMs);

      // Müfettişi çağır
      get().checkAndTriggerReview();

    } catch (err: any) {
      console.error("Submit Answer Failed:", err.message);
      // Fallback
      setTimeout(() => get().nextRound(), 2500);
    }
  },

  nextRound: (forceTime?: number, targetRound?: number) => {
    const { currentRound, totalRounds, questions } = get();
    
    // Calculate the intended next round (either explicit target or current + 1)
    const nextR = targetRound !== undefined ? targetRound : (currentRound + 1);

    // RACE CONDITION PREVENTION: If we are already on or past the target round, abort.
    if (nextR <= currentRound) return;

    if (currentRound >= totalRounds || nextR > totalRounds) {
      // GAME OVER
      const finalState = get();
      const totalScore = finalState.scores[finalState.playerId || ''] || 0;
      trackGameCompleted(
        finalState.selectedRealm,
        finalState.gameMode === 'solo',
        totalScore,
        finalState.totalRounds > 0 ? finalState.correctAnswers / finalState.totalRounds : 0
      );
      // Persist this match's real answer sequence into the device-local
      // Atlas accumulation (guests get a local Atlas; also an offline cache).
      saveMatchToLocalAtlas(finalState.answerLog);
      // A survival run's depth is its real score, and it lives locally like
      // the Atlas does: totalRounds was pinned to the run's true length when
      // the last life went.
      if (finalState.matchMode === 'SURVIVAL') {
        try {
          const prev = Number(localStorage.getItem('wot_survival_best') || '0');
          if (finalState.totalRounds > prev) {
            localStorage.setItem('wot_survival_best', String(finalState.totalRounds));
          }
        } catch { /* storage blocked; the run still counts everywhere else */ }
      }
      // The Daily remembers itself locally: today's result gates the home
      // card, and the streak is consecutive played days (played, not won:
      // showing up is the habit the streak rewards).
      if (finalState.matchMode === 'DAILY') {
        try {
          const key = dailyKey();
          const prev = JSON.parse(localStorage.getItem('wot_daily_v1') || 'null');
          const yesterday = dailyKey(new Date(Date.now() - 86_400_000));
          const streak = prev?.key === yesterday ? (prev.streak || 0) + 1
            : prev?.key === key ? prev.streak
            : 1;
          localStorage.setItem('wot_daily_v1', JSON.stringify({
            key,
            score: totalScore,
            correct: finalState.correctAnswers,
            total: finalState.totalRounds,
            streak,
          }));
        } catch { /* storage blocked; the game itself is unaffected */ }
        // Today is done: its reminder goes, tomorrow's names the streak.
        void refreshReminders();
      }
      // Persist the ending on the server too. Until this existed, every
      // multiplayer match stayed in 'countdown' for ever and no finished game
      // was distinguishable from one that hung. The badges are counted after
      // it, because a room's winner (DUELIST) is decided by finish_match.
      void (async () => {
        await get().finishMatch();
        await get().claimBadges();
      })();
      set({ phase: 'match_complete', view: 'results' });
      return;
    }

    // The round clock, by the running mode's rules (shared/src/modes.ts).
    // One call, no local band table: this block used to carry its own copy of
    // the CLIMB numbers and drifted from the spec.
    const nextQuestion = questions[nextR - 1];
    const mode = get().matchMode;
    const durationMs = roundClockSeconds(mode, nextQuestion?.difficulty as 'easy' | 'medium' | 'hard' | undefined, nextR) * 1000;
    const endsAt = forceTime || (Date.now() + durationMs);

    const snap = get();
    set({
        // The just-finished review made these totals public; the board can
        // now carry them through the next active round.
        boardMyScore: snap.playerId ? (snap.scores[snap.playerId] ?? 0) : 0,
        boardOppScore: snap.opponentScore,
        phase: 'round_active',
        currentRound: nextR,
        currentQuestion: questions[nextR - 1],
        selectedAnswer: null,
        answerLocked: false,
        opponentAnswered: false, // Yeni round başladı, rakip taze!
        opponentLastCorrect: null, // this round's opponent result not in yet
        roundResult: null,
        timerEndsAt: endsAt, // Senkronize edilmiş round saati (roundClockSeconds)
        roundStartedAt: Date.now() // local reaction-time anchor (per device)
    });

    // Ana zaman dayatması: Eger Local tetiklendiysek ve çok oyunculuysak, rakibe 'Geç!' diye dayat!
    if (!forceTime && get().gameMode !== 'solo') {
        const channel = (window as any).activeGameChannel;
        if (channel) {
            channel.send({
                type: 'broadcast',
                event: 'sync_next_round',
                payload: { roundNum: nextR, timerEndsAt: endsAt }
            });
        }
    }
  },

  startSoloMatch: async (realmId?: string) => {
    const { nickname, selectedRealm, selectedMode } = get();
    // The arena: 'ALL', a bucket id, or a single realm (custom).
    const arena = realmId || selectedRealm;
    set({ view: 'lobby', phase: 'waiting', error: null });

    try {
      // Two layers of anti-repeat. The local list is the old one and stays as
      // a same-session guard; deviceId is the durable one, because the server
      // can read the full answer history off it and a reinstall no longer
      // wipes the memory.
      const recentQuestionIds = getRecentQuestionIds();
      const { data: sess } = await supabase.auth.getSession();

      const data = await invokeFunction('start-solo', {
        nickname: nickname || getGuestName(), mode: selectedMode, bucket: arena,
        recentQuestionIds,
        deviceId: getDeviceId(),
        // The token is what proves the account; the bare id is only the
        // fallback claim old servers read.
        access_token: sess?.session?.access_token ?? null,
        authUserId: sess?.session?.user?.id ?? null,
      });

      if (data.error) throw new Error(data.error);

      // Save new question IDs to anti-repeat history
      const newIds = data.questions.map((q: any) => q.id).filter(Boolean);
      saveRecentQuestionIds(newIds);

      const mode = (data.mode ?? selectedMode) as MatchModeId;
      const first = data.questions[0];
      // The first round's clock, by the mode's rules. Later rounds get theirs
      // in nextRound from the same source.
      const firstSeconds = roundClockSeconds(mode, first?.difficulty as 'easy' | 'medium' | 'hard' | undefined, 1);

      set({
        roomId: data.matchId,
        playerId: data.playerId,
        seatToken: data.seatToken ?? null,
        questions: data.questions,
        currentRound: 1,
        totalRounds: data.questions.length,
        currentQuestion: first,
        phase: 'round_active',
        timerEndsAt: Date.now() + firstSeconds * 1000,
        roundStartedAt: Date.now(),
        answerLog: [],
        reviewLog: [],
        view: 'game',
        gameMode: 'solo',
        matchMode: mode,
        matchBucket: data.bucket ?? arena,
        livesLeft: mode === 'SURVIVAL' ? MODES.SURVIVAL.lives : null,
        boardMyScore: 0,
        boardOppScore: 0,
        streak: 0,
        streakBonusTotal: 0,
        badgeSync: null,
      });

      // Analytics: game started (mode rides in the difficulty slot)
      trackGameStarted(arena, true, mode, data.questions.length);
    } catch (err: any) {
      console.error("AI Gen Failed:", err);
      // A failed start is invisible to the game tables (no match row); the
      // event log is the only place it shows up.
      logAppEvent('match_start_failed', { mode: selectedMode, error: String(err?.message ?? err).slice(0, 160) });
      set({ error: err.message, view: 'config' });
    }
  },

  // ── The Daily ─────────────────────────────────────────────────────
  // Same ten questions as everyone else today, in the same order. No arena,
  // no anti-repeat filtering (a shared set must not differ per player), and
  // the played-today memory lives client-side in wot_daily_v1.
  startDailyMatch: async () => {
    const { nickname } = get();
    set({ view: 'lobby', phase: 'waiting', error: null });

    try {
      // The Daily is the one mode a guest can play without ever signing in,
      // so it is also the one that must carry the install's id. Sent neither,
      // resolve_player has nothing to match on and opens a brand new player
      // row for every play: the streak resets and the history is orphaned.
      const { data: sess } = await supabase.auth.getSession();
      const data = await invokeFunction('start-solo', {
        nickname: nickname || getGuestName(), mode: 'DAILY', bucket: 'ALL',
        deviceId: getDeviceId(),
        access_token: sess?.session?.access_token ?? null,
        authUserId: sess?.session?.user?.id ?? null,
      });
      if (data.error) throw new Error(data.error);

      const first = data.questions[0];
      const firstSeconds = roundClockSeconds('DAILY', first?.difficulty as 'easy' | 'medium' | 'hard' | undefined, 1);

      set({
        roomId: data.matchId,
        playerId: data.playerId,
        seatToken: data.seatToken ?? null,
        questions: data.questions,
        currentRound: 1,
        totalRounds: data.questions.length,
        currentQuestion: first,
        phase: 'round_active',
        timerEndsAt: Date.now() + firstSeconds * 1000,
        roundStartedAt: Date.now(),
        answerLog: [],
        reviewLog: [],
        view: 'game',
        gameMode: 'solo',
        matchMode: 'DAILY',
        matchBucket: 'ALL',
        livesLeft: null,
        boardMyScore: 0,
        boardOppScore: 0,
        streak: 0,
        streakBonusTotal: 0,
        badgeSync: null,
      });

      trackGameStarted('DAILY', true, 'DAILY', data.questions.length);
    } catch (err: any) {
      console.error("Daily start failed:", err);
      logAppEvent('match_start_failed', { mode: 'DAILY', error: String(err?.message ?? err).slice(0, 160) });
      set({ error: err.message, view: 'home' });
    }
  },

  createRoom: async () => {
    const { nickname, selectedRealm } = get();

    // Multiplayer requires a signed-in account (guests are Solo-only).
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      set({ error: 'Sign in to create a room.', view: 'home' });
      return;
    }

    set({ view: 'lobby', phase: 'waiting', error: null });

    try {
      // Rooms play RUSH or CLIMB; the setup screen already folds SURVIVAL
      // back to CLIMB before this can run.
      const roomMode = get().selectedMode === 'SURVIVAL' ? 'CLIMB' : get().selectedMode;
      const data = await invokeFunction('create-room', {
        nickname: nickname || getGuestName(),
        mode: roomMode, bucket: selectedRealm,
        access_token: session.access_token,
        // Without this the server resolves the player by account alone and
        // leaves users.device_id null, so the same install looks like a new
        // stranger to every path that only has a device id (join-room seats,
        // the solo answer history).
        deviceId: getDeviceId(),
      });

      if (data.error) throw new Error(data.error);

      set({
        roomId: data.matchId,
        playerId: data.playerId,
        seatToken: data.seatToken ?? null,
        inviteCode: data.inviteCode,
        questions: data.questions,
        totalRounds: data.questions.length,
        answerLog: [],
        reviewLog: [],
        streak: 0,
        streakBonusTotal: 0,
        badgeSync: null,
        isHost: true,
        opponentPresent: false,
        localReady: false,
        opponentReady: false,
        matchStartAt: null,
        matchMode: (data.mode as MatchModeId) ?? null,
        matchBucket: data.bucket ?? null,
        livesLeft: null,
        // Stated rather than inherited. The presence handler returns early on
        // a solo game, so a stale 'solo' left over from a previous match makes
        // the host deaf to the opponent arriving and it waits for ever.
        // quick_match is preserved because it is also a multiplayer mode and
        // the rest of the app distinguishes the two.
        gameMode: get().gameMode === 'quick_match' ? 'quick_match' : 'create_room',
      });

      // Join the channel and announce ourselves. Nothing starts until the
      // handshake sees a second seat.
      get().initListeners();
    } catch (err: any) {
      console.error("Create Room Failed:", err);
      set({ error: err.message, view: 'home' });
    }
  },

  joinRoom: async (inviteCode: string, via?: 'link' | 'code') => {
    // A guest arriving by link has no nickname yet; settle it here so the
    // versus screen and the HUD show the same name the opponent sees.
    const nickname = get().nickname || getGuestName();
    set({ nickname });

    // Joining needs no account: an invite link or a code is the gate, and a
    // guest's seat is tied to the install (device id). Starting a match
    // (quick match, create room) still asks for sign-in.
    const { data: { session } } = await supabase.auth.getSession();

    set({ view: 'lobby', phase: 'waiting', error: null, gameMode: 'create_room' });

    try {
      const data = await invokeFunction('join-room', {
        nickname: nickname || getGuestName(), inviteCode,
        access_token: session?.access_token ?? null,
        deviceId: getDeviceId(),
      });

      if (data.error) throw new Error(data.error);

      // The guest used to jump straight into round one here and fire a
      // one-shot "match_start" at the host. If the host had not finished
      // subscribing, that broadcast was simply lost: the guest played alone
      // against an opponent who never answered, and the host sat in the lobby
      // for ever. Nothing recovered it, because a broadcast is an event and
      // events have no memory.
      //
      // Now the guest only takes its seat. initListeners does the handshake,
      // and neither side starts until both are confirmed present.
      set({
        roomId: data.matchId,
        playerId: data.playerId,
        seatToken: data.seatToken ?? null,
        questions: data.questions,
        totalRounds: data.questions.length,
        currentRound: 0,
        answerLog: [],
        reviewLog: [],
        streak: 0,
        streakBonusTotal: 0,
        badgeSync: null,
        gameMode: 'create_room', // Ensures the multiplayer branch is active
        isHost: false,
        opponentPresent: false,
        localReady: false,
        opponentReady: false,
        matchStartAt: null,
        // The room's recipe travels with the join response, so the guest
        // runs the same clocks as the host; null from a pre-mode room.
        matchMode: (data.mode as MatchModeId) ?? null,
        matchBucket: data.bucket ?? null,
        livesLeft: null,
      });

      // A seat taken from a friend's link or code: the friend's TRAVEL_COMPANION
      // counts it once this player has answered something in the room.
      if (via) logAppEvent('invite_join', { match: data.matchId, via });

      get().initListeners();
      get().resolveOpponent();
    } catch (err: any) {
      console.error("Join Room Failed:", err);
      set({ error: err.message, view: 'home' });
    }
  },

  quickMatch: async () => {
    // Quick match is always RUSH, always ALL realms: six easy and one
    // medium, no hard question, one fast clock, every realm in the draw.
    // A stranger's first duel is the moment a new player decides whether
    // to stay; the CLIMB summit question (a coin flip for most) and the
    // arena rotation (two of the five arenas are built from the sternest
    // realms) both cost that moment. One format and one arena also keep a
    // small player base in a single pool, which is what makes matches
    // findable at all. The daily arena rotation stays in shared for the
    // day the base is large enough to split; nothing else reads it now.
    const arena = 'ALL';
    set({
      view: 'lobby', phase: 'waiting', error: null, gameMode: 'quick_match',
      selectedRealm: arena, selectedMode: 'RUSH'
    });

    try {
      // Search for an existing waiting room. Only consider FRESH rooms
      // (created in the last 2 minutes): without this, quick match joins
      // stale abandoned rooms and the player "plays against nobody".
      // Mode and bucket must match: joining a room with different rules
      // than the button promised is worse than opening a new one, and rooms
      // from pre-mode clients (null mode) are skipped for the same reason.
      const freshCutoff = new Date(Date.now() - 2 * 60 * 1000).toISOString();
      const { data: openMatch } = await supabase
         .from('matches')
         .select('invite_code')
         .eq('status', 'waiting')
         .eq('is_solo', false)
         .eq('mode', 'RUSH')
         .eq('bucket', arena)
         .gte('created_at', freshCutoff)
         .order('created_at', { ascending: false })
         .limit(1)
         .maybeSingle();

      if (openMatch && openMatch.invite_code) {
         await get().joinRoom(openMatch.invite_code);
         return;
      }
    } catch (e) {
      console.warn("Queue check notice:", e);
    }

    // No waiting room found -> Create one
    await get().createRoom();
  },

  // Authoritative opponent lookup from the DB. Independent of the peer's build
  // or broadcast timing: as long as I run this, I learn the opponent's real
  // name + score even if they are on an older client that doesn't broadcast
  // enriched payloads. RLS allows public read of match_players + users.
  resolveOpponent: async () => {
    const { roomId, playerId, gameMode } = get();
    if (!roomId || !playerId || gameMode === 'solo') return;
    try {
      const { data } = await supabase
        .from('match_players')
        .select('id, total_score, users ( nickname )')
        .eq('match_id', roomId);
      if (!data) return;
      const opp = data.find((p: any) => p.id !== playerId);
      if (!opp) return;
      const nick = Array.isArray((opp as any).users)
        ? (opp as any).users[0]?.nickname
        : (opp as any).users?.nickname;
      set((state) => ({
        opponentId: opp.id,
        opponentNickname: nick ?? state.opponentNickname,
        // never let a refresh regress a higher live score from a broadcast
        opponentScore: Math.max(state.opponentScore, (opp as any).total_score || 0),
      }));
    } catch (e) {
      console.warn('resolveOpponent failed:', e);
    }
  },

  // Leaving a room the player is still alone in has to retire it, or it stays
  // 'waiting' for ever and quick match keeps handing it to the next person, who
  // then plays against a host that closed the tab. The two minute freshness
  // filter in quickMatch was a patch over this; the room itself is the leak.
  abandonRoomIfAlone: async () => {
    const { roomId, gameMode, opponentPresent } = get();
    if (!roomId || gameMode === 'solo' || opponentPresent) return;
    try {
      await supabase.rpc('abandon_waiting_match', { p_match_id: roomId });
    } catch (e) {
      // A room left behind is a nuisance, not a failure worth blocking on.
      console.warn('abandon_waiting_match failed:', e);
    }
  },

  // ── The one door onto the versus screen ─────────────────────────────
  // Three things can notice that the second seat is filled: a presence sync, a
  // poll of the seats table, and the lobby screen itself. Earlier each decided
  // for itself what to do about it, which is how one client ended up counting
  // down while the other sat in the lobby. They now all call this, and it is
  // idempotent, so the first one to notice wins and the rest are harmless.
  //
  // Nothing starts from here. The screen this opens asks each player for
  // READY, and only the pair of answers starts the countdown; an earlier
  // version began counting the moment the seat filled, which played as a match
  // that starts itself before anyone agreed to it.
  beginVersus: (opponentNickname) => {
    const state = get();
    if (!state.roomId || state.gameMode === 'solo') return;
    if (state.phase !== 'waiting' && state.phase !== 'countdown') return;

    const patch: Record<string, unknown> = { opponentPresent: true };
    if (opponentNickname) patch.opponentNickname = opponentNickname;
    if (state.phase === 'waiting') {
      patch.phase = 'countdown';
      patch.view = 'versus';
    }
    set(patch as never);
  },

  // The player's half of the handshake. The declaration that counts is the
  // database write inside pollReadyState; the presence track alongside it is
  // only the fast path for the opponent's badge, and losing it costs nothing.
  declareReady: () => {
    const state = get();
    if (state.gameMode === 'solo' || state.localReady) return;
    if (state.phase !== 'countdown') return;

    set({ localReady: true });
    const channel = (window as any).activeGameChannel;
    channel?.track({
      playerId: state.playerId, nickname: state.nickname || getGuestName(),
      isHost: state.isHost, ready: true,
    });
    void get().pollReadyState(true);
  },

  // ── The handshake's source of truth ─────────────────────────────────
  // Ready and the start instant lived on the Realtime channel first, and live
  // testing killed that design: both players' READY flags were observably on
  // the server while each client still showed the other as waiting. The
  // database poll never missed all evening, so the agreement lives where the
  // poll can see it.
  //
  // The database also picks the instant. Neither client proposes one: the
  // second declaration arms round_one_at server-side, and each client
  // converts it to its own clock using the server_now returned beside it, so
  // neither player's clock is trusted with anything.
  pollReadyState: async (declare = false) => {
    const state = get();
    if (!state.roomId || !state.playerId || state.gameMode === 'solo') return;
    if (state.matchStartAt) return;
    // A declaration is never throttled; it is the click itself.
    if (!declare) {
      if (Date.now() - lastReadyPoll < POLL_GAP_MS) return;
      lastReadyPoll = Date.now();
    }

    try {
      const { data, error } = await supabase.rpc('ready_handshake', {
        p_match_id: state.roomId, p_player_id: state.playerId, p_declare: declare,
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) return;

      if (row.opponent_ready && !get().opponentReady) set({ opponentReady: true });

      if (row.starts_at && !get().matchStartAt) {
        // Both stamps come from the same server read, so their difference is
        // free of clock skew; anchoring it to our own Date.now() converts it.
        // The floor covers a poll that arrives after the armed instant.
        const offset = new Date(row.starts_at).getTime() - new Date(row.server_now).getTime();
        const startAt = Date.now() + Math.max(400, offset);
        set({ matchStartAt: startAt });
        get().scheduleMatchStart(startAt);
      }
    } catch (e) {
      // The next tick retries; a failed poll only costs its interval.
      console.warn('ready_handshake failed:', e);
    }
  },

  // The seat is a row in the database, not only an entry in a presence map.
  // Reading the row cannot be missed the way a socket event can, so this is
  // the floor under the handshake: whatever happens to the channel, a player
  // who is waiting will still find an opponent who has actually sat down.
  checkForOpponent: async () => {
    const state = get();
    if (!state.roomId || state.gameMode === 'solo') return;
    if (state.opponentPresent || state.phase !== 'waiting') return;
    if (Date.now() - lastSeatCheck < POLL_GAP_MS) return;
    lastSeatCheck = Date.now();

    try {
      const { data } = await supabase
        .from('match_players')
        .select('id, users ( nickname )')
        .eq('match_id', state.roomId);
      if (!data || data.length < 2) return;

      const opp = data.find((p: any) => p.id !== state.playerId);
      if (!opp) return;
      const nick = Array.isArray((opp as any).users)
        ? (opp as any).users[0]?.nickname
        : (opp as any).users?.nickname;

      get().beginVersus(nick ?? null);
    } catch (e) {
      // A failed poll is not a failed match; the next tick tries again.
      console.warn('checkForOpponent failed:', e);
    }
  },

  // The match ends on each client independently, so both call this and the
  // second call is a no-op. Nothing is awaited on the way to the results
  // screen: the player has finished playing, and a slow write should not sit
  // between them and their score.
  finishMatch: async () => {
    const { roomId, gameMode } = get();
    if (!roomId || gameMode === 'solo') return;
    try {
      await supabase.rpc('finish_match', { p_match_id: roomId });
    } catch (e) {
      console.warn('finish_match failed:', e);
    }
  },

  // Nothing waits on this: the results screen shows the badges when they
  // arrive, and a failure (offline, or the server without 20261012) shows
  // none. The count is cumulative, so a missed one is caught next game.
  claimBadges: async () => {
    const seat = get().playerId;
    if (!seat) return;
    const sync = await awardBadges(seat);
    // A player who has already started another game keeps that game's state.
    if (get().playerId === seat) set({ badgeSync: sync });
  },

  leaveMatch: async () => {
    const s = get();
    const channel = (window as any).activeGameChannel;
    if (s.gameMode !== 'solo' && channel && s.view === 'game') {
      try {
        await Promise.race([
          channel.send({ type: 'broadcast', event: 'player_left', payload: { playerId: s.playerId } }),
          new Promise((r) => setTimeout(r, 800)),
        ]);
      } catch { /* the watchdog on the other side covers a lost message */ }
    }
    get().resetGame();
  },

  resetGame: () => {
    void get().abandonRoomIfAlone();
    get().cleanupListeners();
    set({
      view: 'home',
      gameMode: null,
      roomId: null,
      seatToken: null,
      inviteCode: null,
      phase: 'waiting',
      players: [],
      currentRound: 0,
      scores: {},
      correctAnswers: 0,
      totalResponseTimeMs: 0,
      questions: [],
      currentQuestion: null,
      selectedAnswer: null,
      answerLocked: false,
      opponentAnswered: false,
      opponentLeft: false,
      opponentId: null,
      opponentNickname: null,
      opponentScore: 0,
      opponentLastCorrect: null,
      isHost: false,
      opponentPresent: false,
      localReady: false,
      opponentReady: false,
      matchStartAt: null,
      matchMode: null,
      matchBucket: null,
      livesLeft: null,
      boardMyScore: 0,
      boardOppScore: 0,
      roundResult: null,
      matchResult: null,
      timerEndsAt: null,
      roundStartedAt: null,
      countdownValue: null,
      answerLog: [],
      reviewLog: [],
      streak: 0,
      streakBonusTotal: 0,
      badgeSync: null,
      error: null,
    });
  },

  // ─── Supabase Realtime ────────────
  initListeners: () => {
    const { roomId } = get();
    if (!roomId) return;

    get().cleanupListeners(); // ensure no duplicates

    // ── Why presence and not a broadcast ──────────────────────────
    // A broadcast is an event: if the peer has not finished subscribing when
    // it is sent, it is gone and nothing replays it. That is precisely how a
    // match used to start for one player and never for the other.
    //
    // Presence is state. Every member publishes theirs, the server reconciles,
    // and any client that joins late receives the whole picture on its first
    // sync. Ordering stops mattering, which is the only property that actually
    // fixes this.
    const channel = supabase.channel(`game:${roomId}`, {
      config: { presence: { key: get().playerId ?? crypto.randomUUID() } },
    });

    // ── Reconciliation, not reaction ──────────────────────────────
    // This ran only on presence events at first, and that shipped a deadlock:
    // both players' READY flags were sitting on the server, observably there,
    // while both clients showed the opponent as waiting. An event had been
    // missed or its effect wiped, and nothing ever read the state again.
    // presenceState() is a local snapshot kept current by the socket, so
    // reading it is free; this now also runs on a timer until the countdown is
    // armed, which turns "did we catch the moment" into "we converge within a
    // couple of seconds".
    //
    // True the first time the opponent is seen on the channel itself. Presence
    // lagging behind the database is normal for a few seconds after a join, so
    // absence from presence must not be read as departure until presence has
    // actually shown them once. Resetting on mere absence is what flicked
    // players back to the lobby for a frame after pressing READY.
    let peerSeenOnChannel = false;

    const applyPresence = () => {
      const state = get();
      if (state.gameMode === 'solo') return;

      const raw = channel.presenceState() as Record<string, Array<Record<string, unknown>>>;
      const seats = Object.values(raw).flat();
      const peer = seats.find(m => m.playerId && m.playerId !== state.playerId);

      if (!peer) {
        // Only meaningful as a departure if presence ever showed them. Before
        // that, the database is ahead of the channel and this is just lag.
        if (peerSeenOnChannel && state.opponentPresent && state.phase === 'countdown') {
          peerSeenOnChannel = false;
          set({ opponentPresent: false, localReady: false, opponentReady: false,
                matchStartAt: null, phase: 'waiting', view: 'lobby' });
        } else if (peerSeenOnChannel && !state.opponentLeft && state.view === 'game') {
          // Walked out mid-match. The rest of the match is played alone and
          // the round review no longer waits for an answer that never comes.
          set({ opponentLeft: true });
          get().checkAndTriggerReview();
        }
        return;
      }
      peerSeenOnChannel = true;

      if (peer.playerId) set({ opponentId: peer.playerId as string });
      // Fast path only. The database poll carries the same fact and cannot be
      // missed; this merely makes the badge turn sooner when presence is well.
      if (peer.ready === true && !get().opponentReady) set({ opponentReady: true });

      get().beginVersus((peer.nickname as string) ?? null);
      get().resolveOpponent();
    };

    // The floor under the whole handshake. Before the versus screen it looks
    // for the opponent's row in the database; on the versus screen it asks the
    // database for the ready state and the armed instant, with a presence
    // re-read alongside for the faster badge. A missed event costs one tick,
    // not the match, and everything here goes quiet once the countdown arms.
    (window as any).seatPollTimer = setInterval(() => {
      const s = get();
      if (s.gameMode === 'solo' || !s.roomId) return;
      if (s.phase === 'waiting') void s.checkForOpponent();
      else if (s.phase === 'countdown' && !s.matchStartAt) {
        applyPresence();
        void s.pollReadyState();
      }
    }, 2000);

    channel
      .on('presence', { event: 'sync' }, applyPresence)
      .on('presence', { event: 'join' }, applyPresence)
      .on('presence', { event: 'leave' }, applyPresence)
      .on('broadcast', { event: 'player_left' }, (payload) => {
        const senderId = payload.payload?.playerId;
        const state = get();
        if (senderId && senderId === state.playerId) return;
        if (state.view !== 'game' || state.opponentLeft) return;
        set({ opponentLeft: true });
        get().checkAndTriggerReview();
      })
      .on('broadcast', { event: 'player_answered' }, (payload) => {
        const { playerId: senderId, nickname, correct, totalScore } = payload.payload;
        const state = get();
        if (senderId !== state.playerId) {
          set({
            opponentAnswered: true,
            opponentId: senderId,
            opponentNickname: nickname ?? state.opponentNickname,
            opponentScore: typeof totalScore === 'number' ? totalScore : state.opponentScore,
            opponentLastCorrect: typeof correct === 'boolean' ? correct : state.opponentLastCorrect,
          });
          get().checkAndTriggerReview();
        }
      })
      .on('broadcast', { event: 'sync_next_round' }, (payload) => {
        const { roundNum, timerEndsAt } = payload.payload;
        const state = get();
        if (state.currentRound < roundNum) {
          // Jump to the peer's round, not merely one forward. A client that
          // missed a broadcast is more than one behind, and advancing by one
          // per message leaves the two screens permanently a round apart.
          get().nextRound(timerEndsAt, roundNum);
        } else if (state.currentRound === roundNum && state.timerEndsAt !== timerEndsAt) {
          set({ timerEndsAt });
        }
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          const state = get();
          channel.track({
            playerId: state.playerId,
            nickname: state.nickname || getGuestName(),
            isHost: state.isHost,
            ready: state.localReady,
            startAt: state.matchStartAt ?? null,
          });
        }
      });

    (window as any).activeGameChannel = channel;
  },

  // Arms the single transition into round one. Idempotent on purpose: presence
  // syncs repeatedly and each sync would otherwise queue another timer.
  scheduleMatchStart: (startAt: number) => {
    const pending = (window as any).matchStartTimer;
    if (pending?.at === startAt) return;
    if (pending?.id) clearTimeout(pending.id);

    const fire = () => {
      const state = get();
      if (state.phase !== 'countdown' || !state.questions.length) return;
      // Round one's clock follows the room's mode: a RUSH room runs its flat
      // fast clock from the first question, not only from the second.
      const first = state.questions[0];
      const seconds = state.matchMode === 'RUSH'
        ? MODES.RUSH.timerSeconds.easy
        : first?.difficulty === 'hard' ? 10
        : first?.difficulty === 'medium' ? 12
        : 15;
      const timerEndsAt = Date.now() + seconds * 1000;
      set({
        phase: 'round_active',
        currentRound: 1,
        currentQuestion: first,
        timerEndsAt,
        roundStartedAt: Date.now(),
        view: 'game',
        boardMyScore: 0,
        boardOppScore: 0,
      });
    };

    const delay = startAt - Date.now();
    // A start instant already in the past means this client arrived late, so
    // it catches up rather than waiting out a negative timeout.
    (window as any).matchStartTimer = { at: startAt, id: setTimeout(fire, Math.max(0, delay)) };
  },

  cleanupListeners: () => {
    // Disarm the countdown first. Without this a player who leaves the versus
    // screen is still dropped into round one when the timer fires.
    const pending = (window as any).matchStartTimer;
    if (pending?.id) clearTimeout(pending.id);
    (window as any).matchStartTimer = null;

    // The seat poll outlives the channel otherwise, and would keep querying
    // for an opponent in a room the player has already left.
    const poll = (window as any).seatPollTimer;
    if (poll) clearInterval(poll);
    (window as any).seatPollTimer = null;

    const channel = (window as any).activeGameChannel;
    if (channel) {
      supabase.removeChannel(channel);
      (window as any).activeGameChannel = null;
    }
  },
}));
